import type React from "react";
import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  type RegisterOptions,
  useController,
} from "react-hook-form";

import type { MediaPickerButtonProps } from "@/components/Button/MediaPickerButton";
import { DocumentPickerButton } from "@/components/Button/MediaPickerButton";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";

interface FormDocumentPickerProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<MediaPickerButtonProps<FormDocument[]>, "onChange" | "value"> {
  name: TName;
  defaultValue?: FieldPathValue<TFormValues, TName>;
  rules?: RegisterOptions<TFormValues, TName>;
  "data-testid"?: string;
}

export function FormDocumentPicker<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
  name,
  defaultValue,
  rules,
  "data-testid": dataTestid,
  ...props
}: FormDocumentPickerProps<TFormValues, TName>): React.ReactNode {
  const { field, fieldState } = useController<TFormValues, TName>({ name, rules, defaultValue });

  return (
    <DocumentPickerButton
      {...props}
      name={name}
      isError={fieldState.invalid}
      onChange={field.onChange}
      value={field.value}
    />
  );
}
